import { useCallback } from 'react';
import { FJ110, FJ111 } from '../../../services/j201Api';

/**
 * J201 金額計算 hook
 *
 * BR-C01: 未稅金額 = 數量 * 未稅售價; 含稅金額 = 數量 * 含稅售價
 * BR-C02: 總額加總到主表 fj110_18/181/19
 * BR-C03: 不收費時所有售價/金額歸零
 */
export function useJ201Calculation() {
  /** Recalculate a single detail row's amounts */
  const calcDetailRow = useCallback((d: Partial<FJ111>): Partial<FJ111> => {
    const qty = d.fj111_13 || 0;
    const unitPrice = d.fj111_15 || 0;
    const unitPriceTax = d.fj111_151 || 0;
    return {
      ...d,
      fj111_16: Math.round(qty * unitPrice * 100) / 100,
      fj111_161: Math.round(qty * unitPriceTax * 100) / 100,
    };
  }, []);

  /** Recalculate all detail rows + master totals */
  const recalcAll = useCallback((
    formData: Partial<FJ110>,
    details: Partial<FJ111>[],
  ): { master: Partial<FJ110>; details: Partial<FJ111>[] } => {
    const isCharged = formData.fj110_16 !== false; // true or undefined => charged

    let newDetails: Partial<FJ111>[];
    if (!isCharged) {
      // BR-C03: zero out all prices/amounts when not charged
      newDetails = details.map(d => ({
        ...d,
        fj111_15: 0,
        fj111_151: 0,
        fj111_16: 0,
        fj111_161: 0,
      }));
    } else {
      newDetails = details.map(d => calcDetailRow(d));
    }

    // BR-C02: sum to master
    let sumExTax = 0;
    let sumInTax = 0;
    for (const d of newDetails) {
      sumExTax += d.fj111_16 || 0;
      sumInTax += d.fj111_161 || 0;
    }
    const tax = Math.round((sumInTax - sumExTax) * 100) / 100;

    return {
      master: {
        ...formData,
        fj110_18: Math.round(sumExTax * 100) / 100,
        fj110_181: Math.round(sumInTax * 100) / 100,
        fj110_19: tax,
        fj110_32: newDetails.length,
      },
      details: newDetails,
    };
  }, [calcDetailRow]);

  return { calcDetailRow, recalcAll };
}
